import React, { useEffect, useState, useCallback } from "react";
import { Select, TreeSelect, Button, Input, notification, Spin, Empty, Tooltip } from "antd";
import {
    PlusOutlined,
    DeleteOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    ArrowLeftOutlined,
    ThunderboltOutlined,
    EditOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import { getIndustryTopicLanguageName } from "util/locale";
import styles from "./journeySettings.module.scss";

let keySeq = 1;
const nextKey = () => `k${keySeq++}`;

const emptyStep = () => ({ key: nextKey(), id: null, name: "", topic_lib_ids: [] });
const emptyStage = () => ({ key: nextKey(), id: null, name: "", steps: [emptyStep()] });
const emptyLifecycle = () => ({
    key: nextKey(),
    id: null,
    name: "",
    stages: [emptyStage()],
});

const hydrateLifecycles = (tree) =>
    (tree || []).map((lc) => ({
        key: nextKey(),
        id: lc.id ?? null,
        name: lc.name || "",
        stages: (lc.stages || []).map((st) => ({
            key: nextKey(),
            id: st.id ?? null,
            name: st.name || "",
            steps: (st.steps || []).map((sp) => ({
                key: nextKey(),
                id: sp.id ?? null,
                name: sp.name || "",
                topic_lib_ids: sp.topic_lib_ids || [],
            })),
        })),
    }));

/**
 * Industry Topic Library tree for Konular: parents and nested children.
 * Parent and child can be selected independently (optional child under parent).
 */
const industryTopicTree = (nodes) => {
    if (!Array.isArray(nodes)) return [];
    return nodes
        .filter((node) => node && node.id != null && node.name)
        .map((node) => {
            const children = industryTopicTree(node.child || node.children || []);
            return {
                value: node.id,
                title: node.name,
                ...(children.length > 0 ? { children } : {}),
            };
        });
};

/** Normalize TreeSelect (treeCheckStrictly) value to plain id list. */
const topicIdsFromTreeValue = (vals) =>
    (vals || []).map((v) => (v && typeof v === "object" ? v.value : v));

/** Format stored topic_lib_ids for TreeSelect with treeCheckStrictly. */
const treeValueFromTopicIds = (ids) =>
    (ids || []).map((id) => ({ value: id }));

/**
 * Reorder helper: returns a new array with the item at `index` moved by `delta`.
 */
const move = (arr, index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= arr.length) return arr;
    const copy = [...arr];
    const [item] = copy.splice(index, 1);
    copy.splice(target, 0, item);
    return copy;
};

/**
 * Settings surface for mapping industry Topic Library topics onto the
 * lifecycle -> stage -> step hierarchy that powers the Customer Journey chart.
 *
 * Used both as the first-run wizard (mode="wizard") and as the edit panel
 * (mode="edit"). Persists the whole tree via users/cj/structure/save.
 */
const JourneySettings = ({
    theme = "dark",
    mode = "edit",
    dashboards = [],
    selectedDashboards = [],
    onDashboardsChange,
    onSaved,
    onCancel,
}) => {
    const { t, i18n } = useTranslation();
    const isDark = theme === "dark";

    const [lifecycles, setLifecycles] = useState([emptyLifecycle()]);
    const [topicOptions, setTopicOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    /** True when org already has a saved CJ tree — hides first-setup AI button. */
    const [hasPersistedStructure, setHasPersistedStructure] = useState(false);
    /** Wizard onboarding: selection cards vs manual builder form. */
    const [setupView, setSetupView] = useState("selection");

    const fetchTopics = useCallback(async () => {
        const language = getIndustryTopicLanguageName(i18n.language);

        const summaryRes = await apiRequest(
            "library/industry_topics/summary",
            "POST",
            { language },
            true
        );
        const defaultIndustryId = summaryRes?.success?.default_industry?.id;

        // Topic Library is central/abstract — not scoped to selected dashboards.
        const res = await apiRequest(
            "library/industry_topics/all",
            "POST",
            {
                language,
                filter: "az",
                num_topics: 1000,
                start_index: 0,
                ...(defaultIndustryId ? { industry: defaultIndustryId } : {}),
            },
            true
        );
        setTopicOptions(industryTopicTree(res?.success?.topics));
    }, [i18n.language]);

    const fetchStructure = useCallback(async () => {
        const res = await apiRequest("users/cj/structure", "POST", {}, true);
        if (res?.success && Array.isArray(res.success) && res.success.length > 0) {
            setHasPersistedStructure(true);
            setLifecycles(hydrateLifecycles(res.success));
        } else {
            setHasPersistedStructure(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            await Promise.all([fetchTopics(), fetchStructure()]);
            if (!cancelled) setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [fetchTopics, fetchStructure]);

    const isWizardOnboarding = mode === "wizard" && !hasPersistedStructure;
    const showSelectionView = isWizardOnboarding && setupView === "selection";
    const showManualForm = !isWizardOnboarding || setupView === "manual_form";

    useEffect(() => {
        if (!isWizardOnboarding) {
            setSetupView("manual_form");
        }
    }, [isWizardOnboarding]);

    const handleAiSuggest = async () => {
        setSuggesting(true);
        try {
            const res = await apiRequest(
                "users/cj/structure/suggest",
                "POST",
                { language: getIndustryTopicLanguageName(i18n.language) },
                true
            );
            const draft = res?.success?.lifecycles;
            if (Array.isArray(draft) && draft.length > 0) {
                setLifecycles(hydrateLifecycles(draft));
                notification.success({
                    message: t("customer_journey.cj_settings.ai_suggest_ok"),
                    description: t(
                        "customer_journey.cj_settings.ai_suggest_ok_desc"
                    ),
                });
                return true;
            }
            notification.error({
                message: t("common.error"),
                description:
                    res?.error ||
                    t("customer_journey.cj_settings.ai_suggest_err"),
            });
            return false;
        } catch (e) {
            notification.error({
                message: t("common.error"),
                description: t("customer_journey.cj_settings.ai_suggest_err"),
            });
            return false;
        } finally {
            setSuggesting(false);
        }
    };

    const handleAiSetupStart = async () => {
        const ok = await handleAiSuggest();
        if (ok) setSetupView("manual_form");
    };

    // ---- tree mutation helpers (immutable) ----
    const updateLifecycles = (updater) => setLifecycles((prev) => updater(prev));

    const setLifecycleName = (lcIdx, name) =>
        updateLifecycles((prev) =>
            prev.map((lc, i) => (i === lcIdx ? { ...lc, name } : lc))
        );

    const addLifecycle = () =>
        updateLifecycles((prev) => [...prev, emptyLifecycle()]);

    const deleteLifecycle = (lcIdx) =>
        updateLifecycles((prev) => prev.filter((_, i) => i !== lcIdx));

    const moveLifecycle = (lcIdx, delta) =>
        updateLifecycles((prev) => move(prev, lcIdx, delta));

    const mapStages = (lcIdx, fn) =>
        updateLifecycles((prev) =>
            prev.map((lc, i) => (i === lcIdx ? { ...lc, stages: fn(lc.stages) } : lc))
        );

    const setStageName = (lcIdx, stIdx, name) =>
        mapStages(lcIdx, (stages) =>
            stages.map((st, i) => (i === stIdx ? { ...st, name } : st))
        );

    const addStage = (lcIdx) =>
        mapStages(lcIdx, (stages) => [...stages, emptyStage()]);

    const deleteStage = (lcIdx, stIdx) =>
        mapStages(lcIdx, (stages) => stages.filter((_, i) => i !== stIdx));

    const moveStage = (lcIdx, stIdx, delta) =>
        mapStages(lcIdx, (stages) => move(stages, stIdx, delta));

    const mapSteps = (lcIdx, stIdx, fn) =>
        mapStages(lcIdx, (stages) =>
            stages.map((st, i) => (i === stIdx ? { ...st, steps: fn(st.steps) } : st))
        );

    const setStepName = (lcIdx, stIdx, spIdx, name) =>
        mapSteps(lcIdx, stIdx, (steps) =>
            steps.map((sp, i) => (i === spIdx ? { ...sp, name } : sp))
        );

    const setStepTopics = (lcIdx, stIdx, spIdx, topic_lib_ids) =>
        mapSteps(lcIdx, stIdx, (steps) =>
            steps.map((sp, i) => (i === spIdx ? { ...sp, topic_lib_ids } : sp))
        );

    const addStep = (lcIdx, stIdx) =>
        mapSteps(lcIdx, stIdx, (steps) => [...steps, emptyStep()]);

    const deleteStep = (lcIdx, stIdx, spIdx) =>
        mapSteps(lcIdx, stIdx, (steps) => steps.filter((_, i) => i !== spIdx));

    const moveStep = (lcIdx, stIdx, spIdx, delta) =>
        mapSteps(lcIdx, stIdx, (steps) => move(steps, spIdx, delta));

    const validate = () => {
        const named = lifecycles.filter((lc) => lc.name.trim());
        if (named.length === 0) {
            return t("customer_journey.cj_settings.err_no_lifecycle");
        }
        for (const lc of named) {
            for (const st of lc.stages.filter((s) => s.name.trim())) {
                if (st.steps.filter((sp) => sp.name.trim()).length === 0) {
                    return t("customer_journey.cj_settings.err_empty_stage");
                }
            }
        }
        return null;
    };

    const handleSave = async () => {
        const err = validate();
        if (err) {
            notification.warning({ message: err });
            return;
        }
        setSaving(true);
        try {
            const payload = {
                lifecycles: lifecycles
                    .filter((lc) => lc.name.trim())
                    .map((lc) => ({
                        name: lc.name.trim(),
                        stages: lc.stages
                            .filter((st) => st.name.trim())
                            .map((st) => ({
                                name: st.name.trim(),
                                steps: st.steps
                                    .filter((sp) => sp.name.trim())
                                    .map((sp) => ({
                                        name: sp.name.trim(),
                                        topic_lib_ids: sp.topic_lib_ids || [],
                                    })),
                            })),
                    })),
            };
            const res = await apiRequest(
                "users/cj/structure/save",
                "POST",
                payload,
                true
            );
            if (res && !res.error) {
                notification.success({
                    message: t("common.success"),
                    description: t("common.updated"),
                });
                if (onSaved) onSaved();
            } else {
                notification.error({
                    message: t("common.error"),
                    description: res?.error || t("common.general_error"),
                });
            }
        } catch (e) {
            notification.error({
                message: t("common.error"),
                description: t("common.general_error"),
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className={styles.loading}>
                <Spin size="large" spinning />
            </div>
        );
    }

    return (
        <div className={`${styles.settings} ${isDark ? styles.dark : styles.light}`}>
            {/* Data sources */}
            {onDashboardsChange && (
                <section className={styles.block}>
                    <h3 className={styles.blockTitle}>
                        {t("customer_journey.cj_settings.sources_title")}
                    </h3>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        style={{ width: "100%", maxWidth: 520 }}
                        placeholder={t("common.searchAndSelect")}
                        value={selectedDashboards}
                        onChange={onDashboardsChange}
                        maxTagCount="responsive"
                        filterOption={(input, option) =>
                            (option?.label || "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        options={dashboards.map((d) => ({
                            value: d.id,
                            label: d.name,
                        }))}
                    />
                </section>
            )}

            {showSelectionView && (
                <section className={`${styles.setupSelection} ${styles.viewFadeIn}`}>
                    <p className={styles.setupSelectionLead}>
                        {t("customer_journey.cj_settings.wizard_subtitle")}
                    </p>
                    <div className={styles.selectionCards}>
                        <article
                            className={`${styles.setupCard} ${styles.setupCardAi}`}
                        >
                            <div className={styles.setupCardIcon}>
                                <ThunderboltOutlined />
                            </div>
                            <h3 className={styles.setupCardTitle}>
                                {t("customer_journey.cj_settings.setup_ai_title")}
                            </h3>
                            <p className={styles.setupCardDesc}>
                                {t("customer_journey.cj_settings.setup_ai_desc")}
                            </p>
                            <Button
                                type="primary"
                                size="large"
                                icon={<ThunderboltOutlined />}
                                loading={suggesting}
                                onClick={handleAiSetupStart}
                                className={styles.setupCardBtn}
                            >
                                {t("customer_journey.cj_settings.setup_ai_btn")}
                            </Button>
                        </article>

                        <article
                            className={`${styles.setupCard} ${styles.setupCardManual}`}
                        >
                            <div className={styles.setupCardIcon}>
                                <EditOutlined />
                            </div>
                            <h3 className={styles.setupCardTitle}>
                                {t("customer_journey.cj_settings.setup_manual_title")}
                            </h3>
                            <p className={styles.setupCardDesc}>
                                {t("customer_journey.cj_settings.setup_manual_desc")}
                            </p>
                            <Button
                                size="large"
                                icon={<EditOutlined />}
                                onClick={() => setSetupView("manual_form")}
                                className={styles.setupCardBtn}
                            >
                                {t("customer_journey.cj_settings.setup_manual_btn")}
                            </Button>
                        </article>
                    </div>
                </section>
            )}

            {showManualForm && (
                <div className={`${styles.manualBuilder} ${styles.viewFadeIn}`}>
                    {isWizardOnboarding && (
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => setSetupView("selection")}
                            className={styles.backToSelection}
                        >
                            {t("customer_journey.cj_settings.back_to_selection")}
                        </Button>
                    )}

                    {/* Hierarchy editor */}
                    <section className={styles.block}>
                        <div className={styles.blockHeader}>
                            <h3 className={styles.blockTitle}>
                                {t("customer_journey.cj_settings.structure_title")}
                            </h3>
                            <span className={styles.blockHint}>
                                {t("customer_journey.cj_settings.structure_hint")}
                            </span>
                        </div>
                        {lifecycles.length === 0 && (
                            <Empty description={t("common.no_data")} />
                        )}

                {lifecycles.map((lc, lcIdx) => (
                    <div key={lc.key} className={styles.lifecycle}>
                        <div className={styles.rowHead}>
                            <span className={styles.levelTag}>
                                {t("customer_journey.cj_settings.lifecycle")}
                            </span>
                            <Input
                                value={lc.name}
                                placeholder={t(
                                    "customer_journey.cj_settings.lifecycle_ph"
                                )}
                                onChange={(e) =>
                                    setLifecycleName(lcIdx, e.target.value)
                                }
                                className={styles.nameInput}
                            />
                            <div className={styles.rowActions}>
                                <Tooltip title={t("customer_journey.cj_settings.move_up")}>
                                    <Button
                                        size="small"
                                        type="text"
                                        icon={<ArrowUpOutlined />}
                                        disabled={lcIdx === 0}
                                        onClick={() => moveLifecycle(lcIdx, -1)}
                                    />
                                </Tooltip>
                                <Tooltip
                                    title={t("customer_journey.cj_settings.move_down")}
                                >
                                    <Button
                                        size="small"
                                        type="text"
                                        icon={<ArrowDownOutlined />}
                                        disabled={lcIdx === lifecycles.length - 1}
                                        onClick={() => moveLifecycle(lcIdx, 1)}
                                    />
                                </Tooltip>
                                <Tooltip title={t("common.delete")}>
                                    <Button
                                        size="small"
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => deleteLifecycle(lcIdx)}
                                    />
                                </Tooltip>
                            </div>
                        </div>

                        <div className={styles.stagesWrap}>
                            {lc.stages.map((st, stIdx) => (
                                <div key={st.key} className={styles.stage}>
                                    <div className={styles.rowHead}>
                                        <span
                                            className={`${styles.levelTag} ${styles.stageTag}`}
                                        >
                                            {t("customer_journey.cj_settings.stage")}
                                        </span>
                                        <Input
                                            value={st.name}
                                            placeholder={t(
                                                "customer_journey.cj_settings.stage_ph"
                                            )}
                                            onChange={(e) =>
                                                setStageName(
                                                    lcIdx,
                                                    stIdx,
                                                    e.target.value
                                                )
                                            }
                                            className={styles.nameInput}
                                        />
                                        <div className={styles.rowActions}>
                                            <Button
                                                size="small"
                                                type="text"
                                                icon={<ArrowUpOutlined />}
                                                disabled={stIdx === 0}
                                                onClick={() =>
                                                    moveStage(lcIdx, stIdx, -1)
                                                }
                                            />
                                            <Button
                                                size="small"
                                                type="text"
                                                icon={<ArrowDownOutlined />}
                                                disabled={
                                                    stIdx === lc.stages.length - 1
                                                }
                                                onClick={() =>
                                                    moveStage(lcIdx, stIdx, 1)
                                                }
                                            />
                                            <Button
                                                size="small"
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() =>
                                                    deleteStage(lcIdx, stIdx)
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className={styles.stepsWrap}>
                                        {st.steps.map((sp, spIdx) => (
                                            <div key={sp.key} className={styles.step}>
                                                <div className={styles.rowHead}>
                                                    <span
                                                        className={`${styles.levelTag} ${styles.stepTag}`}
                                                    >
                                                        {t(
                                                            "customer_journey.cj_settings.step"
                                                        )}
                                                    </span>
                                                    <Input
                                                        value={sp.name}
                                                        placeholder={t(
                                                            "customer_journey.cj_settings.step_ph"
                                                        )}
                                                        onChange={(e) =>
                                                            setStepName(
                                                                lcIdx,
                                                                stIdx,
                                                                spIdx,
                                                                e.target.value
                                                            )
                                                        }
                                                        className={styles.nameInput}
                                                    />
                                                    <div
                                                        className={styles.rowActions}
                                                    >
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            icon={
                                                                <ArrowUpOutlined />
                                                            }
                                                            disabled={spIdx === 0}
                                                            onClick={() =>
                                                                moveStep(
                                                                    lcIdx,
                                                                    stIdx,
                                                                    spIdx,
                                                                    -1
                                                                )
                                                            }
                                                        />
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            icon={
                                                                <ArrowDownOutlined />
                                                            }
                                                            disabled={
                                                                spIdx ===
                                                                st.steps.length - 1
                                                            }
                                                            onClick={() =>
                                                                moveStep(
                                                                    lcIdx,
                                                                    stIdx,
                                                                    spIdx,
                                                                    1
                                                                )
                                                            }
                                                        />
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            danger
                                                            icon={
                                                                <DeleteOutlined />
                                                            }
                                                            onClick={() =>
                                                                deleteStep(
                                                                    lcIdx,
                                                                    stIdx,
                                                                    spIdx
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                                <div className={styles.topicRow}>
                                                    <span
                                                        className={styles.topicLabel}
                                                    >
                                                        {t(
                                                            "customer_journey.cj_settings.mapped_topics"
                                                        )}
                                                    </span>
                                                    <TreeSelect
                                                        treeCheckable
                                                        treeCheckStrictly
                                                        showCheckedStrategy={
                                                            TreeSelect.SHOW_ALL
                                                        }
                                                        allowClear
                                                        showSearch
                                                        treeDefaultExpandAll
                                                        className={
                                                            styles.topicSelect
                                                        }
                                                        dropdownStyle={{
                                                            maxHeight: 360,
                                                            overflow: "auto",
                                                        }}
                                                        placeholder={t(
                                                            "customer_journey.cj_settings.topics_ph"
                                                        )}
                                                        value={treeValueFromTopicIds(
                                                            sp.topic_lib_ids
                                                        )}
                                                        onChange={(vals) =>
                                                            setStepTopics(
                                                                lcIdx,
                                                                stIdx,
                                                                spIdx,
                                                                topicIdsFromTreeValue(
                                                                    vals
                                                                )
                                                            )
                                                        }
                                                        maxTagCount="responsive"
                                                        filterTreeNode={(
                                                            input,
                                                            node
                                                        ) =>
                                                            (
                                                                node?.title ||
                                                                ""
                                                            )
                                                                .toString()
                                                                .toLowerCase()
                                                                .includes(
                                                                    input.toLowerCase()
                                                                )
                                                        }
                                                        treeData={topicOptions}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        <Button
                                            type="dashed"
                                            size="small"
                                            icon={<PlusOutlined />}
                                            onClick={() => addStep(lcIdx, stIdx)}
                                            className={styles.addBtn}
                                        >
                                            {t("customer_journey.cj_settings.add_step")}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            <Button
                                type="dashed"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => addStage(lcIdx)}
                                className={styles.addBtn}
                            >
                                {t("customer_journey.cj_settings.add_stage")}
                            </Button>
                        </div>
                    </div>
                ))}

                <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={addLifecycle}
                    className={styles.addLifecycleBtn}
                >
                    {t("customer_journey.cj_settings.add_lifecycle")}
                </Button>
                    </section>

                    <div className={styles.footer}>
                        {onCancel && (
                            <Button onClick={onCancel} disabled={saving}>
                                {t("common.cancel")}
                            </Button>
                        )}
                        <Button
                            type="primary"
                            onClick={handleSave}
                            loading={saving}
                            disabled={saving}
                        >
                            {mode === "wizard"
                                ? t("customer_journey.cj_settings.finish")
                                : t("common.save")}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JourneySettings;
